import * as Sentry from '@sentry/vue';
import { createSentryPiniaPlugin } from '@sentry/vue';
import posthog from 'posthog-js';
import type { App } from 'vue';
import type { Router } from 'vue-router';

/**
 * Collect comprehensive browser/device metadata
 */
function getBrowserMetadata(): Record<string, any> {
  const nav = navigator as any;
  const conn = nav.connection || nav.mozConnection || nav.webkitConnection;
  const perf = performance as any;

  const meta: Record<string, any> = {
    // Browser
    user_agent: navigator.userAgent,
    language: navigator.language,
    languages: navigator.languages?.join(', '),
    platform: nav.platform || nav.userAgentData?.platform || 'unknown',
    vendor: nav.vendor || 'unknown',
    cookie_enabled: navigator.cookieEnabled,
    do_not_track: nav.doNotTrack,
    // Screen
    screen_width: screen.width,
    screen_height: screen.height,
    screen_color_depth: screen.colorDepth,
    screen_pixel_depth: screen.pixelDepth,
    viewport_width: window.innerWidth,
    viewport_height: window.innerHeight,
    device_pixel_ratio: window.devicePixelRatio,
    // Orientation
    orientation: screen.orientation?.type || 'unknown',
    // Timezone & locale
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    timezone_offset: new Date().getTimezoneOffset(),
    locale: Intl.DateTimeFormat().resolvedOptions().locale,
    // Connection
    online: navigator.onLine,
    connection_type: conn?.effectiveType || 'unknown',
    connection_downlink: conn?.downlink || 'unknown',
    connection_rtt: conn?.rtt || 'unknown',
    connection_save_data: conn?.saveData || false,
    // Hardware
    cpu_cores: navigator.hardwareConcurrency || 'unknown',
    device_memory: nav.deviceMemory ? `${nav.deviceMemory} GB` : 'unknown',
    max_touch_points: navigator.maxTouchPoints || 0,
    // Storage
    local_storage_available: !!window.localStorage,
    session_storage_available: !!window.sessionStorage,
    indexed_db_available: !!window.indexedDB,
    // PDF/media support
    pdf_viewer_enabled: nav.pdfViewerEnabled ?? 'unknown',
    // Page
    page_url: window.location.href,
    page_path: window.location.pathname,
    page_referrer: document.referrer || 'direct',
    page_title: document.title,
  };

  // Performance memory (Chrome only)
  if (perf.memory) {
    meta.js_heap_size_limit = `${Math.round(perf.memory.jsHeapSizeLimit / 1024 / 1024)} MB`;
    meta.js_heap_used = `${Math.round(perf.memory.usedJSHeapSize / 1024 / 1024)} MB`;
    meta.js_heap_total = `${Math.round(perf.memory.totalJSHeapSize / 1024 / 1024)} MB`;
  }

  // Navigation timing
  if (perf.getEntriesByType) {
    const navEntry = perf.getEntriesByType('navigation')[0] as any;
    if (navEntry) {
      meta.page_load_time = Math.round(navEntry.loadEventEnd - navEntry.startTime);
      meta.dom_content_loaded = Math.round(navEntry.domContentLoadedEventEnd - navEntry.startTime);
      meta.dns_lookup = Math.round(navEntry.domainLookupEnd - navEntry.domainLookupStart);
      meta.tcp_connect = Math.round(navEntry.connectEnd - navEntry.connectStart);
      meta.ttfb = Math.round(navEntry.responseStart - navEntry.requestStart);
      meta.navigation_type = navEntry.type; // navigate, reload, back_forward, prerender
    }
  }

  return meta;
}

// OHS public pages carry their only credential in the path: a supplier's paperwork token (which can upload),
// a site code, a shared SWMS code. Monitoring must never record it, so the credential segment is replaced
// before a PostHog event or a Sentry event/transaction leaves the browser, and no replay records these pages.
// Matches at the start of a path, after an origin, after a space ("GET https://...", "pageload /site/...")
// or after a quote (PostHog's $elements_chain writes a clicked link as href="/site/...").
const OHS_PUBLIC_TOKEN_PATH = /((?:^|[\s"]|[a-z][a-z0-9+.-]*:\/\/[^/?#\s"]+)\/(?:paperwork|site|swms)\/)[^/?#\s"]+/i;
// The API calls those pages make carry the same credential.
const OHS_PUBLIC_TOKEN_API = /(\/(?:public\/ohs\/(?:sites|paperwork|swms\/legacy)|shared-documents)\/)[^/?#\s]+/i;
const OHS_PUBLIC_TOKEN_PATH_ALL = new RegExp(OHS_PUBLIC_TOKEN_PATH.source, 'gi');
const OHS_PUBLIC_TOKEN_API_ALL = new RegExp(OHS_PUBLIC_TOKEN_API.source, 'gi');

export function isOhsPublicTokenPath(path: string): boolean {
  return OHS_PUBLIC_TOKEN_PATH.test(path);
}

function redactOhsPublicTokenText(text: string): string {
  return text.replace(OHS_PUBLIC_TOKEN_PATH_ALL, '$1[redacted]').replace(OHS_PUBLIC_TOKEN_API_ALL, '$1[redacted]');
}

// URL-like properties, plus autocapture's element data ($elements, $elements_chain, $el_text), which carries a
// clicked link's href and text. Nested objects are walked too: setPersonProperties sends its $set and $set_once
// inside the event's properties.
export function redactOhsPublicTokens(properties: Record<string, any>): Record<string, any> {
  for (const key of Object.keys(properties)) {
    if (/url|pathname|referrer|^\$el/i.test(key)) {
      properties[key] = scrubOhsPublicTokens(properties[key]);
    } else if (properties[key] && typeof properties[key] === 'object') {
      redactOhsPublicTokens(properties[key]);
    }
  }
  return properties;
}

// The Vue router integration records every route param and query value as its own span attribute
// (params.token, url.path.parameter.token, query.code), where no URL pattern can find it.
const ROUTE_ATTRIBUTE = /^(?:url\.path\.parameter|params|query)\./;
export function dropRouteAttributes(data: Record<string, unknown> | undefined) {
  for (const key of Object.keys(data ?? {})) {
    if (ROUTE_ATTRIBUTE.test(key)) delete data![key];
  }
}

// Sentry carries URLs in many places (transaction name, request, trace data, span descriptions and data,
// breadcrumbs), so every string in the event's plain data is scrubbed. Class instances and the SDK's own
// processing metadata (scopes, which never leave the browser) are not walked.
export function scrubOhsPublicTokens<T>(event: T): T {
  const seen = new WeakSet<object>();
  const scrub = (node: any): any => {
    if (typeof node === 'string') return redactOhsPublicTokenText(node);
    if (!node || typeof node !== 'object' || seen.has(node)) return node;
    const proto = Object.getPrototypeOf(node);
    if (!Array.isArray(node) && proto !== Object.prototype && proto !== null) return node;
    seen.add(node);
    for (const key of Object.keys(node)) {
      if (key === 'sdkProcessingMetadata') continue;
      const scrubbed = scrub(node[key]);
      if (scrubbed !== node[key]) node[key] = scrubbed;
    }
    return node;
  };
  return scrub(event);
}

// GA4's router page view sends the raw path and href, so a public token page's view is sent without the credential.
// Every other page returns null and keeps the plugin's own page view.
export function ohsPublicPageView(to: { name?: unknown; path: string }) {
  if (!isOhsPublicTokenPath(to.path)) return null;
  return { page_title: String(to.name), page_path: scrubOhsPublicTokens(to.path), page_location: scrubOhsPublicTokens(window.location.href) };
}

// A replay records every URL it visits (the replay's URL list bypasses every hook), so recording has to stop
// before the app navigates onto a public token route. Recording stays off for the rest of the page session.
export function stopRecordingOnOhsPublicRoutes(
  router: Pick<Router, 'beforeEach'>,
  getReplay: () => { stop(): Promise<void> } | undefined = Sentry.getReplay,
  posthogClient: { __loaded: boolean; stopSessionRecording(): void } = posthog,
): void {
  router.beforeEach((to) => {
    if (!isOhsPublicTokenPath(to.path)) return;
    void getReplay()?.stop();
    // Only once PostHog has initialised (it is skipped when no key is configured).
    if (posthogClient.__loaded) posthogClient.stopSessionRecording();
  });
}

export let sentryPiniaPlugin: ReturnType<typeof createSentryPiniaPlugin> | null = null;

export function initMonitoring(app: App, router: Router) {
  // Enable monitoring in production and staging (disabled on local dev)
  // Staging should use a separate PostHog project key so it does not pollute prod analytics.
  const mode = import.meta.env.MODE;
  if (mode !== 'production' && mode !== 'staging') {
    return;
  }
  stopRecordingOnOhsPublicRoutes(router);

  // Initialize Sentry (production only — staging should not report to Sentry)
  const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
  if (mode === 'production' && sentryDsn && sentryDsn !== 'your_sentry_dsn_here') {
    const browserMeta = getBrowserMetadata();
    const onOhsPublicTokenRoute = isOhsPublicTokenPath(window.location.pathname);

    Sentry.init({
      app,
      dsn: sentryDsn,
      debug: false,
      integrations: [
        // ── Performance tracing with route instrumentation ──
        Sentry.browserTracingIntegration({
          router,
          enableInp: true,
          // No request span for OHS API calls or the geocoder. A span keeps the full URL,
          // and these carry site codes and worker tokens, or (on an /ohs/ page) the
          // street address of the site being geocoded.
          shouldCreateSpanForRequest: (url) => !/\/ohs\/|maps\.googleapis\.com\/maps\/api\/geocode/i.test(url),
        }),
        // ── Session replay: capture everything unmasked, but never on an OHS public token route ──
        ...(onOhsPublicTokenRoute ? [] : [
          Sentry.replayIntegration({
            maskAllText: false,
            maskAllInputs: false,
            blockAllMedia: false,
            // Capture full network request/response in replay
            networkDetailAllowUrls: [/builtsimple\.com\.au/, /localhost/, /127\.0\.0\.1/],
            // OHS reports (including /site/:code/report), photos, profiles,
            // signatures and worker tokens must never be recorded in replay.
            networkDetailDenyUrls: [
              /\/(?:ohs|site|swms|paperwork|shared-documents|companysettings|getjobbyuser|persssignees|supplierlist|supplier)(?:\/|\?|$)/,
            ],
            networkCaptureBodies: true,
            networkRequestHeaders: ['X-Requested-With', 'Content-Type', 'Authorization', 'Accept'],
            networkResponseHeaders: ['Content-Type', 'X-Request-Id', 'X-RateLimit-Remaining'],
          }),
          // ── Replay canvas: capture charts, signatures, canvas elements ──
          Sentry.replayCanvasIntegration(),
        ]),
        // ── Breadcrumbs: all DOM, console, fetch, XHR, navigation ──
        Sentry.breadcrumbsIntegration({
          console: true,
          dom: { serializeAttribute: ['data-testid', 'data-id', 'id', 'class', 'aria-label'] },
          fetch: true,
          history: true,
          xhr: true,
        }),
        // ── HTTP client: auto-capture 4xx/5xx as events ──
        // Exclude socket.io endpoints from HTTP client error tracking
        Sentry.httpClientIntegration({
          failedRequestStatusCodes: [[400, 421], [423, 599]],
          failedRequestTargets: [
            /builtsimple\.com\.au\/(?!socket\.io)/,  // Match builtsimple.com.au but exclude /socket.io
            /localhost\/(?!socket\.io)/,
            /127\.0\.0\.1\/(?!socket\.io)/,
          ],
        }),
        // ── Console capture: error + warn as Sentry events ──
        Sentry.captureConsoleIntegration({ levels: ['error', 'warn'] }),
        // ── Browser profiling: flame charts for slow pages ──
        Sentry.browserProfilingIntegration(),
        // ── Context lines: show source code around errors ──
        Sentry.contextLinesIntegration({ frameContextLines: 7 }),
        // ── Extra error data: capture non-standard Error properties ──
        Sentry.extraErrorDataIntegration({ depth: 5 }),
        // ── Reporting observer: browser deprecation/intervention reports ──
        Sentry.reportingObserverIntegration({ types: ['crash', 'deprecation', 'intervention'] }),
        // ── Culture context: locale, timezone, calendar info ──
        Sentry.cultureContextIntegration(),
        // ── HTTP context: browser, OS, device from user-agent ──
        Sentry.httpContextIntegration(),
      ],

      // ── Sampling: capture everything ──
      tracesSampleRate: 1.0,
      replaysSessionSampleRate: 1.0,
      replaysOnErrorSampleRate: 1.0,
      profilesSampleRate: import.meta.env.PROD ? 0.5 : 1.0,

      // ── PII & data ──
      sendDefaultPii: true,
      attachStacktrace: true,

      // ── Breadcrumbs ──
      maxBreadcrumbs: 250,

      // ── Environment ──
      environment: import.meta.env.MODE,
      // VITE_APP_VERSION still wins so an explicit value can override, but it
      // is set nowhere -- __BUILD_VERSION__ is what actually fills this in, and
      // is why events now carry a release at all.
      release: import.meta.env.VITE_APP_VERSION || __BUILD_VERSION__ || undefined,

      // ── Tags for filtering ──
      initialScope: {
        tags: {
          app: 'builtsimple-web',
          browser: browserMeta.vendor || 'unknown',
          connection_type: browserMeta.connection_type,
          cpu_cores: String(browserMeta.cpu_cores),
          device_memory: browserMeta.device_memory,
        },
      },

      // ── Enrich every event with full metadata ──
      beforeSend(event) {
        // Health information can be embedded in exception values or captured component props.
        // The router matches paths case-insensitively, so /Ohs/reports is the same page.
        if (/^\/(?:site|ohs|swms|paperwork)(?:\/|$)/i.test(window.location.pathname)) return null;
        // Extract common values used across filters
        const status = event.tags?.api_status || event.contexts?.api_response?.status;
        const message = event.message || event.exception?.values?.[0]?.value || '';
        const url = event.contexts?.trace?.data?.url || event.request?.url || '';
        // Failed OHS API requests (httpClientIntegration and anything else tied to
        // an OHS endpoint) carry site codes, worker tokens and worker data. Never send.
        // Nor anything raised on a worker's /site/<code> page: the enrichment below
        // stamps the page URL, and with it the site code, onto every event. The router
        // matches paths case-insensitively, so /Site/<code> is the same page.
        if (/^\/site(?:\/|$)/i.test(window.location.pathname) ||
            [url, event.request?.url].some((u) => /\/api\/(?:public\/)?ohs\//i.test(String(u || '')))) {
          return null;
        }
        // The geocoder logs the address it searched for, and on an /ohs/ page that is a
        // site's address. captureConsoleIntegration puts it in the message or, when an
        // Error is logged alongside, in extra.arguments.
        if (/^\/ohs\//i.test(window.location.pathname) &&
            [event.message, (event.extra?.arguments as unknown[] | undefined)?.[0]]
              .some((text) => /^(?:No geocode results|Geocoding failed) for "/.test(String(text ?? '')))) {
          return null;
        }
        if (status == '422' || status == 422 || message.includes('API POST 422') ||
            message.includes('API GET 422') || message.includes('API PUT 422') ||
            message.includes('API DELETE 422')) {
          return null;
        }

        // Skip 404 errors - these are "not found" errors that are expected application behavior
        if (status == '404' || status == 404 || message.includes('status code: 404')) {
          return null;
        }

        // Skip 413 Payload Too Large. Same class as 422: the user picked a file
        // bigger than the server accepts, the API says so in plain words and the
        // UI shows it. Nothing to fix in code. Worth stating explicitly because
        // this status only started appearing once EnforceUploadSize began
        // answering oversize uploads honestly — before that they surfaced as a
        // misleading 400 "No files uploaded".
        if (status == '413' || status == 413 || message.includes('status code: 413') ||
            message.includes('API POST 413') || message.includes('API PUT 413')) {
          return null;
        }

        // Skip 409 Conflict - expected "already exists"/duplicate validation that the
        // backend rejects and we surface to the user via a toast (e.g. "A work type
        // with name 'X' already exists"). Same class as 422 — not an actionable bug.
        if (status == '409' || status == 409 ||
            message.includes('API POST 409') || message.includes('API GET 409') ||
            message.includes('API PUT 409') || message.includes('API DELETE 409') ||
            message.includes('status code: 409')) {
          return null;
        }

        // Skip 522/523/524 errors - these are Cloudflare timeout/origin errors (server-side issues)
        if (status == '522' || status == 522 || status == '523' || status == 523 || status == '524' || status == 524) {
          return null;
        }

        // Shared exception metadata used by several filters below.
        const mechType = event.exception?.values?.[0]?.mechanism?.type || '';
        const exceptionType = event.exception?.values?.[0]?.type || '';
        const responseStatus = (event.contexts as any)?.response?.status_code;

        // Skip auto-captured HTTP failures from httpClientIntegration. The fetch
        // wrapper already routes API failures through captureApiError() with full
        // context (request body, response body, network state), so these are pure
        // duplicates. We drop:
        //   • 401/403 — auth handled by the axios interceptor (user redirected to
        //     login); the auto-event carries nothing actionable
        //   • 5xx — backend issues already captured with full context elsewhere
        if (mechType.startsWith('auto.http.client') &&
            typeof responseStatus === 'number' &&
            (responseStatus === 401 || responseStatus === 403 ||
             (responseStatus >= 500 && responseStatus < 600))) {
          return null;
        }

        // Everything else from httpClientIntegration (the 4xx band) is ALSO a
        // duplicate of captureApiError — but we can't just drop it, because a few
        // call sites use raw fetch()/raw axios and never reach captureApiError, so
        // the auto-event is their only report.
        //
        // Instead, give it captureApiError's fingerprint — [method, url, status] —
        // so Sentry FILES IT UNDER THE SAME ISSUE rather than opening a parallel
        // one. One failed upload was producing three issues: this auto-event
        // ("HTTP Client Error with status code: 400"), the captureApiError event
        // ("API POST 400: <url>"), and a captureConsole copy of the caller's
        // console.error. Same incident, same trace id, three groups to triage.
        // Aligning the fingerprint collapses them and loses no coverage.
        if (mechType.startsWith('auto.http.client') && typeof responseStatus === 'number') {
          const failedUrl = event.request?.url || url;
          const failedMethod = (event.request?.method || event.tags?.api_method || 'GET') as string;
          if (failedUrl) {
            event.fingerprint = [failedMethod, failedUrl, String(responseStatus)];
          }
        }

        // Skip captureConsoleIntegration's wrapping of axios errors.
        // These fire when somebody console.error'd an AxiosError (network
        // drop during PDF upload, 4xx/5xx response, axios timeout). We
        // already surface these to the user via toasts and capture API
        // failures with full context via captureApiError() in the fetch
        // wrapper — the captured-console copy in Sentry is duplicate noise.
        if (mechType === 'auto.core.capture_console' &&
            (/AxiosError/i.test(message) ||
             /AxiosError/i.test(exceptionType) ||
             /Network Error/i.test(message) ||
             /Network Error/i.test(exceptionType) ||
             /ERR_NETWORK/i.test(message) ||
             /ECONNABORTED/i.test(message) ||
             /timeout of \d+ms exceeded/i.test(message) ||
             /Request failed with status code \d+/i.test(message))) {
          return null;
        }

        // Vue's default errorHandler does `console.error('[Vue Error] ...', err)`,
        // which captureConsoleIntegration then ships as its own Sentry event.
        // The Sentry Vue integration already captures the same error with proper
        // mechanism metadata (component, lifecycle, props) — the captured-console
        // copy is duplicate noise with a worse title.
        if (mechType === 'auto.core.capture_console' &&
            /\[Vue Error\]/i.test(message)) {
          return null;
        }

        // localStorage/sessionStorage full — QuotaExceededError from caching a
        // large object (e.g. a tender with embedded proposal images) to
        // localStorage. It's caught and handled (the API is the source of
        // truth); the write is retried after pruning, then skipped. Backstop in
        // case any other path console.errors it — not an actionable crash.
        if (/QuotaExceededError/i.test(exceptionType) ||
            /QuotaExceededError/i.test(message) ||
            /NS_ERROR_DOM_QUOTA_REACHED/i.test(message) ||
            /exceeded the quota/i.test(message)) {
          return null;
        }

        // Chunked-upload backend errors surfaced via console.error from
        // ImportWizard/UploadProgress.vue. The chunked uploader uses raw axios
        // (not fetch-wrapper), so these don't go through captureApiError —
        // they're only visible via captureConsoleIntegration. The Lstat /
        // SplFileInfo::getType() failure is a known backend issue (chunk merge
        // path not found) tracked separately; per-occurrence Sentry events
        // aren't actionable on the frontend.
        if (/SplFileInfo::getType\(\): Lstat failed/i.test(message) ||
            (/Upload error:/i.test(message) && /\/storage\/app\/public\/chunks\//i.test(message))) {
          return null;
        }

        // Google Maps deprecation warnings (console.warn from the Maps SDK).
        // We can't fix these without migrating off the deprecated APIs, and they
        // fire on every page that mounts a map — pure noise in Sentry until the
        // migration ships. Tracked separately; un-filter once migrated.
        if (/google\.maps\.Marker is deprecated/i.test(message) ||
            /google\.maps\.marker\.AdvancedMarkerElement/i.test(message) ||
            /google\.maps\.places\.AutocompleteService/i.test(message) ||
            /google\.maps\.places\.Autocomplete is not available to new customers/i.test(message) ||
            /AutocompleteService is not available to new customers/i.test(message) ||
            /Google Maps already loaded outside @googlemaps\/js-api-loader/i.test(message)) {
          return null;
        }

        // Skip CORS wildcard deprecation warnings from browser ReportingObserver
        // This is a Chrome deprecation notice about future CORS behavior, not our bug
        // This is a backend/API server configuration issue, not a frontend code issue
        if (message.includes('Authorization will not be covered by the wildcard symbol') ||
            message.includes('Access-Control-Allow-Headers') ||
            message.includes('ReportingObserver [deprecation]') ||
            (message.includes('CORS') && message.includes('wildcard')) ||
            (message.includes('Authorization') && message.includes('CORS'))) {
          return null;
        }

        // Skip document policy violations - browser security policy warnings
        if (message.includes('Document policy violation') || message.includes('js-profiling is not allowed')) {
          return null;
        }

        // Third-party @pdftron/webviewer (Apryse) internal UI errors. Its
        // React-based viewer UI (webviewer-ui.min.js) intermittently hits a null
        // ref while measuring an element during layout/unmount — e.g.
        // "null is not an object (n.current.getBoundingClientRect)", Safari-only,
        // handled. It's a library-internal race we can't fix in app code. No app
        // code accesses `.current.getBoundingClientRect` (verified), so any such
        // error — or anything originating in the WebViewer bundle — is the
        // viewer's, not ours. Drop as un-actionable noise.
        const frames = (event.exception?.values || []).flatMap(
          (v: any) => v?.stacktrace?.frames || []
        );
        if (/\.current\.getBoundingClientRect/i.test(message) ||
            frames.some((f: any) => /webviewer|pdftron|pdfjs-express/i.test(f?.filename || f?.abs_path || ''))) {
          return null;
        }

        // WebViewer library console warnings (captured via captureConsole), e.g.
        // "[WebViewer] Version Mismatch: Core and UI should be updated to version
        // X". These are emitted by the library itself and reflect an asset/version
        // drift to fix during a WebViewer upgrade — not a per-event frontend bug.
        if (/\[WebViewer\]/i.test(message) &&
            (/Version Mismatch/i.test(message) || /should be updated to version/i.test(message))) {
          return null;
        }

        // pdf-lib parser recovering from a malformed PDF, e.g.
        //   "Trying to parse invalid object: {"line":..,"column":..,"index":..})"
        //   "Invalid object ref: 268 0 R"
        // Emitted by PDFParser.tryToParseInvalidIndirectObject (console.warn), which
        // captureConsoleIntegration then ships as a warning-level issue. The Plans
        // tab on estimate-list loads CUSTOMER-UPLOADED plan sets, and plenty of real
        // PDFs in the wild have a slightly off xref table. pdf-lib loads with
        // throwOnInvalidObject=false, so it repairs the object and carries on — the
        // upload still succeeds. Nothing here is actionable in app code: the fix
        // would be "the customer's PDF is malformed", and the library already
        // handles it. Drop as third-party recovery noise.
        if (/Invalid object ref:/i.test(message) ||
            /Trying to parse invalid object/i.test(message)) {
          return null;
        }

        // N+1 API call for estimate-sublists has been fixed with batched requests
        // Filter out old production errors until new code is deployed
        if (event.transaction === 'dashboard' &&
            url.includes('/estimate-sublists') &&
            url.includes('estimate_id=')) {
          return null;
        }

        // Skip 401 Unauthorized errors - these are authentication issues handled by axios interceptor
        // The user will be automatically redirected to login page
        if (status == '401' || status == 401 ||
            /API (GET|POST|PUT|PATCH|DELETE) 401\b/.test(message) ||
            message.includes('status code: 401') || message.includes('Unauthorized') ||
            message.includes('Unauthenticated')) {
          return null;
        }

        // Skip Xero API errors - Xero integration is work-in-progress, backend not implemented yet.
        // Catch by request URL, by page URL, and by message text (for console.warn/error captures
        // where the request URL isn't on the event but the log message mentions Xero).
        if (url.includes('/xero/') ||
            event.request?.url?.includes('/xero/') ||
            /\bXero\b/i.test(message)) {
          return null;
        }

        // Skip PostHog analytics errors - third-party service, we can't control their API issues
        if (url.includes('posthog.com') || /posthog/i.test(message)) {
          return null;
        }

        // Skip PDFTron/Apryse WebViewer setCustomData type warnings.
        // PDFTron emits `console.warn("setCustomData received a non-string value for X. It will be automatically stringified.")`
        // for every numeric/boolean value. Auto-stringify is benign, and PDFTron itself
        // also calls setCustomData internally during annotation copy/restore/XFDF import,
        // so even pristine app code can't fully prevent it. Filter at Sentry instead of
        // wrapping all 250+ call sites with String().
        if (/setCustomData received a non-string value/i.test(message)) {
          return null;
        }

        // Skip PDFTron/Apryse WebViewer incremental-download fallback warning.
        // Emitted when WebViewer requests Range headers for partial PDF download but the
        // storage origin doesn't return Content-Range (typical for static file servers).
        // WebViewer transparently falls back to a full download — purely informational.
        if (/Could not use incremental download for url/i.test(message) ||
            /Could not retrieve Content-Range header/i.test(message)) {
          return null;
        }

        // Skip generic "Failed to fetch" network errors - these are temporary connectivity issues
        // already handled by the application's error handling (includes Zapier, external webhooks, etc.)
        if (message.includes('Failed to fetch')) {
          return null;
        }

        // Skip notification fetch errors - these are API errors already handled by the application
        if (message.includes('Failed to fetch notifications from backend')) {
          return null;
        }

        // Skip API fetch failures with fallback to stored data - informational about using cache
        if (message.includes('API fetch failed, using stored data')) {
          return null;
        }

        // Skip Socket.IO transport errors — handled by socket.io's built-in reconnect logic.
        // Match case-insensitively on the message and check both event.request.url
        // (XHR target) and the page URL, since the shared `url` variable above prefers
        // event.contexts.trace.data.url. Also catch by exception type (TransportError)
        // and by breadcrumb category for unhandled rejections that bubble up here.
        const lowerMsg = message.toLowerCase();
        const breadcrumbHasSocket = event.breadcrumbs?.some((b) =>
          (b.message || '').toLowerCase().includes('socket.io') ||
          (b.data?.url || '').includes('socket.io')
        );
        if (
          lowerMsg.includes('socket.io') ||
          lowerMsg.includes('xhr poll error') ||
          lowerMsg.includes('websocket error') ||
          lowerMsg.includes('transport error') ||
          lowerMsg.includes('transport close') ||
          url.includes('socket.io') ||
          event.request?.url?.includes('socket.io') ||
          exceptionType === 'TransportError' ||
          breadcrumbHasSocket
        ) {
          return null;
        }

        // Skip Fabric.js image loading errors - these are network/CORS errors already handled
        if (message.includes('fabric: Error loading data') ||
            message.includes('Error loading data:')) {
          return null;
        }

        // Skip Socket.IO max reconnection attempts - this is expected when connection fails
        if (message.includes('Max reconnection attempts reached')) {
          return null;
        }

        // PDF/DXF preview warnings have been changed from console.warn to console.log
        // Keeping this filter commented out in case regressions occur
        // if (message.includes('No selectedPdf or file - skipping PDF preview') ||
        //     message.includes('No selectedPdf or file - skipping DXF preview')) {
        //   return null;
        // }

        // Skip invalid page request errors - these are validation errors from PDF viewer
        if (message.includes('Invalid page request')) {
          return null;
        }

        // Skip annotation loading warnings - these are expected when annotations aren't loaded yet
        if (message.includes('No annotations data available for unit type drawings') ||
            message.includes('Annotation manager not initialized yet') ||
            message.includes('Need to call loadAnnotations first')) {
          return null;
        }

        // Skip PDF scale warnings - informational only, measurements won't be accurate without scale
        if (message.includes('Page 1 has no scale set') ||
            message.includes('has no scale set, measurements will be inaccurate')) {
          return null;
        }

        // Skip DXF/SVG parsing warnings - informational only, some files may have no renderable objects
        if (message.includes('SVG parsed but produced no renderable objects') ||
            message.includes('[DxfViewer] SVG parsed but produced no renderable objects')) {
          return null;
        }

        // Skip annotation library errors - race conditions when annotation classes aren't loaded yet
        if (message.includes('anotations.Color is not a constructor') ||
            message.includes('annotations.Color is not a constructor')) {
          return null;
        }

        // Skip PDFTron WebViewer measurement warnings - emitted by the library itself when
        // setMeasurementUnits / setAnnotationScale runs against annotations missing the
        // intent-type or measure dictionary. Library-internal noise, not actionable in our code.
        if (message.includes('Unable to set scale to an annotation or a tool which has incorrect IT or no measure dictionary') ||
            message.includes('Unable to set precision to an annotation or a tool which has incorrect IT or no measure dictionary')) {
          return null;
        }

        // Skip form initialization errors on Jobs page - these are transient race conditions
        // during form setup that don't affect functionality
        if (event.transaction === 'jobs' &&
            (message.includes("Cannot read properties of undefined (reading 'work_days')") ||
             message.includes("Cannot read properties of undefined (reading 'job_color')") ||
             message.includes("Cannot read properties of undefined (reading 'update_actual_date')") ||
             message.includes("Cannot read properties of undefined (reading 'actual_completion')") ||
             message.includes("Cannot read properties of undefined (reading 'customers')"))) {
          return null;
        }

        // Skip user authentication race condition errors - these occur when checking permissions
        // before the user store is fully initialized
        if (message.includes("Cannot read properties of undefined (reading 'usertype_lbl')")) {
          return null;
        }

        // Skip session keep-alive failures - informational about expired sessions
        if (message.includes('Session keep-alive failed') ||
            message.includes('session may have expired') ||
            message.includes('Authentication error fetching user info')) {
          return null;
        }

        // Skip generic unhandled promise rejections with no useful error details
        // These are typically race conditions or third-party library issues
        if (message.includes('Event `Event` (type=error) captured as promise rejection')) {
          return null;
        }

        // Skip ReportingObserver deprecation reports we can't act on. The
        // "unload event listeners" warning fires from jQuery, jszip and
        // @syncfusion/ej2-data — none of our own code registers unload
        // listeners, so this is purely third-party deprecation noise.
        if (message.includes('ReportingObserver [deprecation]') &&
            message.includes('Unload event listeners')) {
          return null;
        }

        // Skip Vue Router navigation errors that are already handled
        // These are expected navigation failures (cancelled, duplicated, etc.)
        if (event.tags?.error_type === 'vue_error' &&
            event.tags?.vue_lifecycle === 'errorHandler' &&
            (!message || message === 'No error message') &&
            event.tags?.handled === 'yes') {
          return null;
        }

        // Skip validation warnings from third-party libraries (charts, etc.)
        if (message.includes('Expected: array with [0].scaling property')) {
          return null;
        }

        // Skip response structure validation warnings - these are logged for debugging
        if (message.includes('Invalid response structure')) {
          return null;
        }

        // Skip JSON parsing errors from invalid API responses (HTML error pages, etc.)
        const errorType = event.exception?.values?.[0]?.type || '';
        if (message.includes('is not valid JSON') || message.includes('Unexpected token') ||
            (errorType === 'SyntaxError' && message.includes('JSON'))) {
          return null;
        }

        // Skip DXF/PDF viewer cache warnings - these are informational about scale persistence
        if (message.includes('[DxfStore] No cache found') || message.includes('scale will not persist')) {
          return null;
        }

        // Skip DXF viewer SVG rendering warnings - these are expected for empty/invalid DXF files
        if (message.includes('[DxfViewer] SVG parsed but produced no renderable objects') ||
            message.includes('SVG parsed but produced no renderable objects') ||
            message.includes('No renderable objects found in SVG')) {
          return null;
        }

        // Skip browser notification support warnings - expected on mobile or when notifications disabled
        if (message.includes('Browser does not support notifications')) {
          return null;
        }

        // Skip file rejection warnings - validation messages for file uploads
        if (message.includes('File rejected:')) {
          return null;
        }

        // Skip Google Maps invalid coordinate errors - occurs when lat/lng is missing or invalid
        if (message.includes('setCenter: not a LatLng or LatLngLiteral') ||
            message.includes('in property lat: not a number') ||
            message.includes('in property lng: not a number')) {
          return null;
        }

        // Skip "no data loaded" warnings - informational messages about empty states
        if (message.includes('No plans data loaded')) {
          return null;
        }

        // Skip schedule skipping warnings - informational about deleted/invalid schedules
        if (message.includes('Skipping schedule') && message.includes('may be deleted')) {
          return null;
        }

        // Skip schedule fetching errors - already handled by the application
        if (message.includes('Error fetching linked schedule')) {
          return null;
        }

        // Skip job schedule creation errors - already handled by the application
        if (message.includes('Job schedule create error')) {
          return null;
        }

        // Skip component configuration warnings for unknown options
        if (message.includes('Unknown option')) {
          return null;
        }

        // Skip analytics data generation warnings - informational about generating extra weeks
        if (message.includes('weeks from API, generating more weeks for analytics')) {
          return null;
        }

        // Skip "day not found" warnings - informational about date format mismatches in weekDays
        if (message.includes('Day not found in weekDays') || message.includes('Day not found in weekdays')) {
          return null;
        }

        // Skip property creation errors on primitives - these are data type mismatches already handled
        if (message.includes('Cannot create property') && message.includes('on string')) {
          return null;
        }

        // Skip generic [object Object] console errors - no useful debugging information
        if (message === '[object Object]' || message.trim() === '[object Object]') {
          return null;
        }

        // Skip geocoding fallback warnings - these are informational about using mock data
        if ((message.includes('Could not geocode') || message.includes('geocode city')) &&
            (message.includes('using mock data') || message.includes('mock data'))) {
          return null;
        }

        // Skip legacy weather-API-key fallback warning emitted by older deployed bundles
        if (message.includes('Weather API key not configured')) {
          return null;
        }

        // Skip geocoding "no results" errors - these occur when user enters invalid address
        if (message.includes('No results found for address') ||
            message.includes('Could not geocode')) {
          return null;
        }

        // Moment.js deprecation warnings have been fixed by adding explicit format arrays
        // Keeping this filter commented out in case regressions occur
        // if (message.includes('Deprecation warning') && message.includes('moment')) {
        //   return null;
        // }

        // Skip 404/500/522/523/524 API errors - these are server-side or network issues
        if (message.includes('API GET 404') || message.includes('API POST 404') ||
            message.includes('API PUT 404') || message.includes('API DELETE 404') ||
            message.includes('API GET 500') || message.includes('API POST 500') ||
            message.includes('API PUT 500') || message.includes('API DELETE 500') ||
            message.includes('HTTP Client Error with status code: 500') ||
            message.includes('HTTP Client Error with status code: 404') ||
            message.includes('HTTP Client Error with status code: 522') ||
            message.includes('HTTP Client Error with status code: 523') ||
            message.includes('HTTP Client Error with status code: 524') ||
            (message.includes('showschedule') && message.includes('404'))) {
          return null;
        }

        // Skip version check errors - version.json may not exist in dev/staging, already handled by try-catch
        if (url.includes('version.json') || message.includes('version.json')) {
          return null;
        }

        // Live page info
        event.tags = {
          ...event.tags,
          page_url: window.location.href,
          page_path: window.location.pathname,
          viewport: `${window.innerWidth}x${window.innerHeight}`,
          online: String(navigator.onLine),
          orientation: screen.orientation?.type || 'unknown',
          visibility: document.visibilityState,
        };

        // Connection state at time of error
        const conn = (navigator as any).connection;
        const connInfo: Record<string, any> = {
          online: navigator.onLine,
        };
        if (conn) {
          connInfo.effective_type = conn.effectiveType;
          connInfo.downlink = conn.downlink;
          connInfo.rtt = conn.rtt;
          connInfo.save_data = conn.saveData;
        }

        // Performance memory at time of error
        const perf = performance as any;
        const memInfo: Record<string, any> = {};
        if (perf.memory) {
          memInfo.heap_used = `${Math.round(perf.memory.usedJSHeapSize / 1024 / 1024)} MB`;
          memInfo.heap_total = `${Math.round(perf.memory.totalJSHeapSize / 1024 / 1024)} MB`;
          memInfo.heap_limit = `${Math.round(perf.memory.jsHeapSizeLimit / 1024 / 1024)} MB`;
          memInfo.heap_usage_pct = `${Math.round((perf.memory.usedJSHeapSize / perf.memory.jsHeapSizeLimit) * 100)}%`;
        }

        event.contexts = {
          ...event.contexts,
          browser_environment: {
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            language: navigator.language,
            languages: navigator.languages?.join(', '),
            user_agent: navigator.userAgent,
            platform: (navigator as any).platform || 'unknown',
            cookie_enabled: navigator.cookieEnabled,
            do_not_track: (navigator as any).doNotTrack,
          },
          screen_info: {
            screen: `${screen.width}x${screen.height}`,
            viewport: `${window.innerWidth}x${window.innerHeight}`,
            color_depth: screen.colorDepth,
            pixel_ratio: window.devicePixelRatio,
            orientation: screen.orientation?.type || 'unknown',
          },
          hardware: {
            cpu_cores: navigator.hardwareConcurrency || 'unknown',
            device_memory: (navigator as any).deviceMemory ? `${(navigator as any).deviceMemory} GB` : 'unknown',
            max_touch_points: navigator.maxTouchPoints,
          },
          network_state: connInfo,
          memory_state: memInfo,
          page_info: {
            url: window.location.href,
            path: window.location.pathname,
            referrer: document.referrer || 'direct',
            title: document.title,
            visibility: document.visibilityState,
            dom_elements: document.getElementsByTagName('*').length,
          },
          runtime_state: {
            timestamp: new Date().toISOString(),
            app_uptime_ms: Math.round(performance.now()),
          },
        };

        // An error raised inside a navigation carries that span's route attributes too.
        dropRouteAttributes(event.contexts?.trace?.data);
        return scrubOhsPublicTokens(event);
      },

      // ── Transactions: scrub OHS public tokens from URLs, span descriptions and breadcrumbs ──
      // Any route's params and query can be a credential (a paperwork token, a reset code), so the
      // router's route attributes are dropped on every route; the route name and URL still name the page.
      beforeSendTransaction(event) {
        dropRouteAttributes(event.contexts?.trace?.data);
        (event.spans ?? []).forEach((span) => dropRouteAttributes(span.data));
        return scrubOhsPublicTokens(event);
      },

      // ── Standalone spans (INP): sent on their own, so beforeSendTransaction never sees them ──
      // An INP span carries the page's route name as `transaction`, and the pageload span is named
      // after the raw path until the router renames it (an unnamed, unmatched route keeps the path).
      // Its attributes get the same route-attribute drop as a transaction's spans.
      beforeSendSpan(span) {
        dropRouteAttributes(span.data);
        return scrubOhsPublicTokens(span);
      },

      // ── Enrich breadcrumbs ──
      beforeBreadcrumb(breadcrumb) {
        // Sensitive OHS calls also bypass the SDK's automatic fetch/UI breadcrumbs.
        if (/^\/(?:site|ohs|swms|paperwork)(?:\/|$)/i.test(window.location.pathname) ||
            /\/(?:ohs|site|swms|paperwork|shared-documents|companysettings|getjobbyuser|persssignees|supplierlist|supplier)(?:\/|\?|$)/i.test(
                [breadcrumb.data?.url, breadcrumb.data?.from, breadcrumb.data?.to, breadcrumb.message].filter(Boolean).join(' '))) return null;
        breadcrumb.data = {
          ...breadcrumb.data,
          visibility: document.visibilityState,
        };
        // Enrich click breadcrumbs with element details
        if (breadcrumb.category === 'ui.click') {
          breadcrumb.data = {
            ...breadcrumb.data,
            page_path: window.location.pathname,
          };
        }
        return breadcrumb;
      },
    });

    // Set global context with full browser metadata (persists across all events)
    Sentry.setContext('browser_metadata_full', browserMeta);

    // ── Vue error handler: capture with component context ──
    // Every component under src/views/pages/OHS except index.vue (the SWMS generator page at
    // /ohs, which takes no props; its __name is shared by dozens of pages). SwmsDocument and
    // EditableList stay listed: their props carry the SWMS document's content. `__name`
    // survives production builds for <script setup> SFCs; `__file` does not.
    const OHS_COMPONENT_NAMES = new Set(['EditableList', 'InductionBuilder', 'InductionEditor', 'SectionEditor',
      'PublicSwms', 'SwmsDocument', 'EmergencyPlan', 'FacilityPicker', 'PublicSite', 'SiteMap', 'SiteSafety',
      'SiteSafetyCard', 'CompletionStep', 'EmergencyStep', 'ProfileSteps', 'PublicInductionFlow', 'QuestionStep',
      'SectionStep', 'SignatureStep', 'SiteInductionPanel',
      'AttendanceRegister', 'OnSiteCard', 'PublicAttendance',
      'PublicSwmsList', 'PublicSwmsSign', 'SiteSwmsPanel',
      'MyReports', 'NotifiableGuidance', 'PublicReport', 'ReportDetail', 'ReportForm', 'ReportPhotos',
      'ReportsRegister', 'SiteReportsPanel',
      'ComplianceChip', 'ContactAdd', 'EmployerPicker', 'MergeDialog', 'SupplierPicker', 'SupplierReview',
      'SupplierReviewBadge', 'TicketForm', 'TicketPhoto', 'TicketSettings', 'WorkerDetail', 'WorkerTickets',
      'WorkersRegister',
      'CompliancePackDialog', 'OhsOverview',
      'EmployeeSafety', 'ScheduledTradesCard', 'ToolboxTalksCard', 'VendorLinks', 'PublicPaperwork',
      'OhsEmptyState', 'OhsPageHeader', 'OhsSetupGuide', 'SummaryTiles']);
    const isOhsComponent = (instance: any) => {
      // Walk up the tree: a generic child (a text field, a dialog) rendered inside an
      // OHS component can still hold worker data in its props.
      for (let node = instance, depth = 0; node && depth < 50; node = node.$parent, depth++) {
        if (OHS_COMPONENT_NAMES.has(node.$options?.__name) || OHS_COMPONENT_NAMES.has(node.$options?.name) ||
            /\/OHS\/(?!index\.vue$)/i.test(node.$options?.__file || '')) return true;
      }
      return false;
    };
    // On the SWMS generator page an error's message can itself be client data: toApiError
    // (src/utils/axios.ts) makes a non-JSON reply the message, and JSON.parse quotes the text it
    // failed on. So the copy sent from there keeps the name and stack frames under a fixed message,
    // and leaves out the reply an axios Error carries. V8 starts a stack with "Name: message".
    // Any other stack (Firefox and Safari carry no message; a message changed after the throw leaves
    // the old one in V8's header) keeps only its frame lines.
    const SWMS_GENERATOR_ERROR = 'SWMS generator error';
    const STACK_FRAME = /^\s+at \S|^[^\s@]*@\S+:\d+:\d+$/;
    const withoutMessage = (err: Error) => {
      const header = `${err.name}: ${err.message}`;
      const stack = err.stack?.startsWith(header) ? `${err.name}: ${SWMS_GENERATOR_ERROR}${err.stack.slice(header.length)}`
        : err.stack?.split('\n').filter((line) => STACK_FRAME.test(line)).join('\n') || undefined;
      return Object.assign(new Error(SWMS_GENERATOR_ERROR), { name: err.name, stack });
    };
    app.config.errorHandler = (err, instance, info) => {
      // OHS errors can carry worker data in props, route params and the error itself.
      // Every /site, /swms, /paperwork and /ohs/... route; not /ohs or /ohs/, the SWMS generator page.
      if (/^\/(?:site(?:\/|$)|swms(?:\/|$)|paperwork(?:\/|$)|ohs\/(?!$))/i.test(instance?.$route?.path || window.location.pathname) ||
          isOhsComponent(instance)) {
        console.error('[Vue Error] OHS component error');
        return;
      }
      // The SWMS generator page (/ohs) is reported, but its fields, API replies and error
      // messages can be client data (ABN, contacts, document content), so none goes to Sentry.
      const onSwmsGenerator = /^\/ohs\/?$/i.test(instance?.$route?.path || window.location.pathname);
      Sentry.withScope((scope) => {
        scope.setTag('vue_lifecycle', info);
        scope.setTag('error_type', 'vue_error');
        if (instance) {
          const componentName = instance.$options?.name || instance.$options?.__name || 'Anonymous';
          scope.setTag('vue_component', componentName);
          scope.setContext('vue_component_data', {
            component: componentName,
            lifecycle_hook: info,
            // On the generator the query and params are left out with the props.
            route: (onSwmsGenerator ? instance.$route?.path : instance.$route?.fullPath) || 'unknown',
            route_name: instance.$route?.name || 'unknown',
            route_params: onSwmsGenerator ? undefined : instance.$route?.params || {},
            route_query: onSwmsGenerator ? undefined : instance.$route?.query || {},
            props: instance.$props && !onSwmsGenerator
              ? JSON.stringify(instance.$props).substring(0, 1000) : undefined,
          });
        }
        // On the generator only the fixed message goes, never what the thrown value carries.
        if (onSwmsGenerator) {
          if (err instanceof Error) {
            Sentry.captureException(withoutMessage(err));
            return;
          }
          scope.setContext('vue_error_payload', { type: typeof err, value: undefined, read_as: SWMS_GENERATOR_ERROR });
          scope.setFingerprint(['vue-error', instance?.$options?.name || instance?.$options?.__name || 'Anonymous', info, SWMS_GENERATOR_ERROR]);
          Sentry.captureException(new Error(SWMS_GENERATOR_ERROR));
          return;
        }
        Sentry.captureException(err);
      });
      console.error(`[Vue Error] ${info}:`, err);
    };

    // ── Vue warnings (disabled - warnings suppressed globally) ──
    // Warnings are now suppressed in main.ts to reduce console noise
    // if (!import.meta.env.PROD) {
    //   app.config.warnHandler = (msg, instance, trace) => {
    //     Sentry.addBreadcrumb({
    //       category: 'vue.warn',
    //       message: msg,
    //       level: 'warning',
    //       data: { trace: trace?.substring(0, 500) },
    //     });
    //   };
    // }

    // ── Unhandled promise rejections ──
    window.addEventListener('unhandledrejection', (event) => {
      Sentry.withScope((scope) => {
        scope.setTag('error_type', 'unhandled_promise_rejection');
        scope.setContext('promise_rejection', {
          reason: String(event.reason),
          stack: event.reason?.stack?.substring(0, 2000),
          url: window.location.href,
          timestamp: new Date().toISOString(),
        });
        Sentry.captureException(event.reason || new Error('Unhandled Promise Rejection'));
      });
    });

    // ── Global JS errors ──
    window.addEventListener('error', (event) => {
      // Only capture resource loading errors (images, scripts, stylesheets)
      if (event.target && event.target !== window) {
        const target = event.target as HTMLElement;
        Sentry.addBreadcrumb({
          category: 'resource.error',
          message: `Failed to load: ${(target as any).src || (target as any).href || 'unknown'}`,
          level: 'error',
          data: {
            tag: target.tagName,
            src: (target as any).src || (target as any).href,
          },
        });
      }
    }, true); // capture phase to catch resource errors

    // ── Network online/offline ──
    window.addEventListener('offline', () => {
      Sentry.addBreadcrumb({ category: 'connectivity', message: 'User went offline', level: 'warning' });
      Sentry.setContext('network_state', { online: false, changed_at: new Date().toISOString() });
    });
    window.addEventListener('online', () => {
      Sentry.addBreadcrumb({ category: 'connectivity', message: 'User came back online', level: 'info' });
      Sentry.setContext('network_state', { online: true, changed_at: new Date().toISOString() });
    });

    // ── Page visibility changes (tab switch, minimize) ──
    document.addEventListener('visibilitychange', () => {
      Sentry.addBreadcrumb({
        category: 'app.lifecycle',
        message: `Page visibility: ${document.visibilityState}`,
        level: 'info',
        data: { state: document.visibilityState },
      });
    });

    // ── Page before unload (user leaving) ──
    window.addEventListener('beforeunload', () => {
      Sentry.addBreadcrumb({
        category: 'app.lifecycle',
        message: 'Page unloading (user leaving)',
        level: 'info',
        data: { url: window.location.href, uptime_ms: Math.round(performance.now()) },
      });
    });

    // ── Track long tasks (>50ms main thread blocks) ──
    if ('PerformanceObserver' in window) {
      try {
        const longTaskObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (entry.duration > 100) { // Only log tasks > 100ms
              Sentry.addBreadcrumb({
                category: 'performance',
                message: `Long task detected: ${Math.round(entry.duration)}ms`,
                level: 'warning',
                data: {
                  duration_ms: Math.round(entry.duration),
                  start_time: Math.round(entry.startTime),
                  entry_type: entry.entryType,
                },
              });
            }
          }
        });
        longTaskObserver.observe({ entryTypes: ['longtask'] });
      } catch (e) {
        // PerformanceObserver longtask not supported
      }

      // ── Track layout shifts (CLS) ──
      try {
        const clsObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            const layoutShift = entry as any;
            if (layoutShift.value > 0.1) { // Only significant shifts
              Sentry.addBreadcrumb({
                category: 'performance',
                message: `Layout shift: ${layoutShift.value.toFixed(4)}`,
                level: 'warning',
                data: {
                  value: layoutShift.value,
                  had_recent_input: layoutShift.hadRecentInput,
                },
              });
            }
          }
        });
        clsObserver.observe({ entryTypes: ['layout-shift'] });
      } catch (e) {
        // layout-shift not supported
      }
    }

    // ── Create Pinia plugin for store state on errors ──
    sentryPiniaPlugin = createSentryPiniaPlugin({
      attachPiniaState: true,
      addBreadcrumbs: true,
      stateTransformer: (state) => {
        // Remove sensitive data from store state before sending
        const sanitized = { ...state };
        if (sanitized.user) {
          sanitized.user = { ...sanitized.user as any, token: '[REDACTED]', password: '[REDACTED]' };
        }
        return sanitized;
      },
    });

    console.log('Sentry initialized (maximum detail mode)');
  }

  // Initialize PostHog
  const posthogKey = import.meta.env.VITE_POSTHOG_KEY;
  const posthogHost = import.meta.env.VITE_POSTHOG_HOST;
  if (posthogKey && posthogKey !== 'your_posthog_key_here') {
    posthog.init(posthogKey, {
      api_host: posthogHost || 'https://us.i.posthog.com',
      // Disable gzip-js compression and bootstrap the key path explicitly to
      // stop "event submitted without an api_key" 401s. Some early-boot /
      // page-unload / recording-batch events were being flushed with the
      // api_key field missing from the JSON body — both flags eliminate that
      // class of edge case at a negligible bandwidth cost.
      disable_compression: true,
      bootstrap: { distinctID: undefined },
      autocapture: true,
      capture_pageview: true,
      capture_pageleave: true,
      capture_dead_clicks: true,
      // Disabled: heatmaps capture $autocapture mouse-position events that
      // can batch incorrectly under early-boot conditions and produce the
      // same key-less 401s as $web_vitals. Heatmap data is a low-value
      // analytics extra; turning it off keeps the console clean.
      enable_heatmaps: false,
      enable_recording_console_log: true,
      // Disabled: PostHog's exception capture registers a global error
      // handler that fires before init completes, producing events with a
      // missing api_key field that PostHog then rejects with a 401. Sentry
      // already captures exceptions with richer stack traces and breadcrumbs,
      // so this was duplicate work anyway.
      capture_exceptions: false,
      // Disabled: Web Vitals observers (LCP/FID/CLS/INP) fire within the
      // first few hundred ms of page load — often before posthog-js finishes
      // binding the api_key to its global state. Those events get serialised
      // as bare arrays without the {api_key, batch:[...]} wrapper PostHog
      // requires and end up as 401s. Confirmed by inspecting the failing
      // payload — it was a $web_vitals event with no api_key field.
      // Sentry can capture web vitals too if needed.
      capture_performance: false,
      // A replay's page snapshot carries the full href, which before_send can't reach; never record a public OHS page.
      disable_session_recording: OHS_PUBLIC_TOKEN_PATH.test(window.location.pathname),
      session_recording: {
        recordCrossOriginIframes: true,
        // Mask all form inputs by default to protect customer data
        // (project costs, client details, financial info, etc.)
        maskAllInputs: true,
        maskInputOptions: {
          password: true,
          email: true,
          tel: true,
          number: true,
          search: false,
        },
        // Sample 20% of sessions to reduce storage cost while keeping enough
        // data for behavioural analysis. Errors are still captured 100% via Sentry.
        sampleRate: 0.2,
        recordHeaders: false,
        recordBody: false,
      },
      // Strip sensitive properties from every event before it leaves the browser.
      // Strip sensitive properties from every event before it leaves the browser.
      //
      // Do NOT add 'token' here. posthog-js carries the project api_key in
      // properties.token, so denylisting it strips the key out of the event's own
      // payload and every capture comes back 401 "event submitted without an
      // api_key". That is what silently killed all web analytics from 2026-04-20
      // until 2026-08-14: init succeeded, /flags/ succeeded, and every single
      // event was rejected.
      before_send: (event) => {
        if (event?.properties) redactOhsPublicTokens(event.properties);
        if (event?.$set) redactOhsPublicTokens(event.$set);
        if (event?.$set_once) redactOhsPublicTokens(event.$set_once);
        return event;
      },
      property_denylist: [
        '$ip',
        'password',
        'csrfToken',
        'authorization',
        'creditCard',
        'card_number',
        'cvv',
        'ssn',
        'tfn',
      ],
      rageclick: true,
      persistence: 'localStorage+cookie',
      // Tag every event with the build mode so we can distinguish
      // staging vs production traffic inside PostHog.
      loaded: (ph) => {
        ph.register({
          app_environment: mode,
          app_name: 'builtsimple-web',
        });
      },
    });
    console.log(`PostHog initialized (${mode} mode)`);
  }
}

/**
 * Optional user profile properties that get attached to PostHog and Sentry
 * when a user logs in. Anything you pass here becomes a "person property" in
 * PostHog and is queryable across every event that user generates.
 *
 * Pass whatever the backend returns on the user object — only defined fields
 * are sent. Add new fields here when the API starts returning them.
 */
export interface UserProfileProperties {
  company_name?: string;
  company_id?: string | number;
  user_role?: string;
  subscription_tier?: string;
  subscription_status?: string;
  signup_date?: string;
  is_admin?: boolean;
  is_owner?: boolean;
  team_size?: number;
  trial_ends_at?: string;
  preferred_currency?: string;
  country?: string;
}

export function identifySentryUser(
  userId: string,
  email?: string,
  username?: string,
  profile: UserProfileProperties = {}
) {
  Sentry.setUser({
    id: userId,
    email: email || undefined,
    username: username || undefined,
  });
  Sentry.addBreadcrumb({
    category: 'auth',
    message: `User identified: ${email || username || userId}`,
    level: 'info',
  });
  Sentry.setContext('user_profile', {
    id: userId,
    email: email || undefined,
    username: username || undefined,
    ...profile,
    identified_at: new Date().toISOString(),
  });

  try {
    // Strip undefined values so PostHog person properties stay clean
    const personProps: Record<string, any> = {
      email: email || undefined,
      name: username || undefined,
      identified_at: new Date().toISOString(),
    };
    Object.entries(profile).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        personProps[key] = value;
      }
    });
    Object.keys(personProps).forEach((k) => {
      if (personProps[k] === undefined) delete personProps[k];
    });

    posthog.identify(userId, personProps);

    // Company group analytics: lets PostHog aggregate events per company
    // (funnels/retention by builder org, not just by individual user).
    if (profile.company_id) {
      posthog.group('company', String(profile.company_id), {
        name: profile.company_name || undefined,
        signup_date: profile.signup_date || undefined,
        subscription_tier: profile.subscription_tier || undefined,
        subscription_status: profile.subscription_status || undefined,
        team_size: profile.team_size || undefined,
        trial_ends_at: profile.trial_ends_at || undefined,
        country: profile.country || undefined,
      });
    }
  } catch (e) {
    // PostHog may not be initialized
  }
}

export function resetSentryUser() {
  Sentry.addBreadcrumb({
    category: 'auth',
    message: 'User logged out',
    level: 'info',
  });
  Sentry.setUser(null);
  Sentry.setContext('user_profile', null);

  try {
    posthog.reset();
  } catch (e) {
    // PostHog may not be initialized
  }
}

/**
 * Add a custom breadcrumb for tracking user actions
 */
export function addSentryBreadcrumb(
  category: string,
  message: string,
  data?: Record<string, any>,
  level: 'info' | 'warning' | 'error' = 'info'
) {
  Sentry.addBreadcrumb({ category, message, data, level });
}

/**
 * Track a user action (button click, form submit, modal open, etc.)
 */
export function trackUserAction(action: string, details?: Record<string, any>) {
  Sentry.addBreadcrumb({
    category: 'user.action',
    message: action,
    data: { ...details, page: window.location.pathname, timestamp: new Date().toISOString() },
    level: 'info',
  });
}

/**
 * Capture an API error with full request/response context
 */
export function captureApiError(
  url: string,
  method: string,
  status: number,
  responseData: any,
  requestBody?: any
) {
  // Get connection state at time of error
  const conn = (navigator as any).connection;
  const networkState: Record<string, any> = { online: navigator.onLine };
  if (conn) {
    networkState.effective_type = conn.effectiveType;
    networkState.downlink = conn.downlink;
    networkState.rtt = conn.rtt;
  }

  // Skip 422 errors (used for validation responses like invalid login,
  // password, etc.) and 401s (auth handled by axios interceptor — user
  // is redirected to login automatically; surfacing in Sentry is noise).
  // 413 joins them: an oversize upload is the user's file, not our bug. The
  // API returns a message naming the size and the cap, and the caller shows it.
  if (status === 422 || status === 401 || status === 413) {
    return;
  }

  // Best-effort endpoints whose failures are already handled by the caller
  // and don't affect the user. Reporting them to Sentry just adds noise when
  // shared infra (sanctum/cache/DB) has a transient blip.
  if (url.includes('/session-ping')) {
    return;
  }

  Sentry.withScope((scope) => {
    scope.setTag('error_type', 'api_error');
    scope.setTag('api_status', String(status));
    scope.setTag('api_method', method);
    scope.setTag('api_endpoint', url);
    scope.setContext('api_request', {
      url,
      method,
      body: requestBody ? JSON.stringify(requestBody).substring(0, 4000) : undefined,
      timestamp: new Date().toISOString(),
    });
    scope.setContext('api_response', {
      status,
      data: responseData !== undefined ? JSON.stringify(responseData).substring(0, 4000) : undefined,
    });
    scope.setContext('network_at_error', networkState);
    scope.setFingerprint([method, url, String(status)]);
    Sentry.captureException(
      new Error(`API ${method} ${status}: ${url}`)
    );
  });
}
